// ../data/books/DarkForest/data/cap4.js
export default {
  title: "Capítulo 4 — Descargas: ¿cómo no caer en trampas?",
  sections: [
    {
      heading: "¿Por qué “descargar” no es tan simple?",
      text: [
        "Antes de entrar al mundo blockchain, mucha gente ya perdió todo por descargar apps falsas o versiones alteradas. Creían que estaban instalando la wallet correcta… pero habían caído en una trampa diseñada para robarles.",
        "Los dos problemas más comunes:"
      ],
      list: [
        "No encontrar el sitio oficial o la tienda correcta y terminar instalando una billetera falsa.",
        "No verificar si el archivo fue manipulado (**tampered**) antes de instalarlo."
      ],
      note:
        "Descargar no es solo hacer clic en un botón. Es un paso que define si todo lo que viene después será sólido… o estará comprometido desde el origen."
    },

    {
      heading: "Encontrar el sitio oficial",
      text: [
        "Antes de descargar cualquier billetera, software o extensión Web3, necesitas estar seguro de que el sitio es **verdaderamente oficial**. Existen copias idénticas visualmente pero diseñadas para robar claves.",
        "Técnicas útiles para validar:"
      ],
      list: [
        "Buscar en Google, pero con cuidado con los resultados patrocinados.",
        "Entrar por agregadores reconocidos como **CoinMarketCap** o **CoinGecko** para saltar al sitio oficial del proyecto.",
        "Pedir la URL a gente de confianza y comparar.",
        "La verdad debe converger: si tres fuentes confiables apuntan a la misma URL, probablemente es la correcta."
      ],
      note:
        "Nunca ingreses una seed phrase, clave privada ni dirección desde un enlace enviado por mensaje desconocido (email, Telegram, X, WhatsApp...)."
    },

    {
      heading: "Verificar integridad del archivo (consistencia)",
      text: [
        "Incluso cuando descargas desde el sitio oficial, **el archivo puede haber sido manipulado en el camino**. Verificar la integridad evita ataques tipo **man-in-the-middle** o versiones modificadas con malware.",
        "Existen dos métodos principales:"
      ],
      list: [
        "**Cálculo de hash** (preferiblemente SHA-256).",
        "**Verificación de firma GPG** del release oficial."
      ],
      subsections: [
        {
          heading: "Comprobación por hash (SHA-256)",
          text: [
            "Descarga el archivo y la suma oficial (publicada en el sitio). No copies hashes desde foros desconocidos ni blogs externos.",
            "Tu misión es comparar el hash calculado localmente con el hash oficial publicado."
          ],
          pre: `# macOS / Linux
shasum -a 256 archivo.dmg
# o
openssl dgst -sha256 archivo.dmg

# Windows (PowerShell)
Get-FileHash .\\archivo.exe -Algorithm SHA256

# Compara la salida con el SHA-256 publicado en el sitio oficial. Deben coincidir exactamente.`
        },
        {
          heading: "Verificación con GPG",
          text: [
            "Muchos proyectos publican la firma (.asc o .sig) y la clave pública. Importa la clave, verifica la huella (fingerprint) desde varias fuentes y después valida la firma.",
            "Este método es más fuerte que el hash, porque impide que una persona suplante al autor legítimo."
          ],
          pre: `# Importar la clave pública (ejemplo)
gpg --keyserver keyserver.ubuntu.com --recv-keys <FINGERPRINT_O_KEYID>

# Mostrar y verificar fingerprint
gpg --fingerprint <FINGERPRINT_O_KEYID>

# Verificar el release firmado
gpg --verify archivo.asc archivo.zip

# Debes ver 'Good signature' y que proviene del keyid correcto (fingerprint verificado).`
        },
        {
          heading: "Ejemplo a seguir",
          text: [
            "Algunas billeteras como **Electrum**, **MetaMask (desktop release)** o **Ledger Live** publican siempre instrucciones claras de verificación (hash + GPG).",
            "Toma esos flujos como referencia al evaluar otros proyectos. Si un proyecto no ofrece mecanismos de verificación, es una señal de alerta."
          ]
        }
      ]
    },

    {
      heading: "Diferencias por tipo de billetera",
      text: [
        "No todas las billeteras se descargan igual, ni se protegen igual. Dependiendo de tu objetivo (pruebas, trading, custodia a largo plazo) la fuente oficial, el tipo de archivo y la verificación de seguridad variarán.",
        "Regla general: **billeteras calientes para uso diario; billeteras frías para conservación de patrimonio.**"
      ],
      table: {
        columns: ["Tipo", "Dónde conseguirla", "Atención clave", "Uso recomendado"],
        rows: [
          {
            "Tipo": "PC (desktop)",
            "Dónde conseguirla": "Siempre desde el sitio oficial",
            "Atención clave": "Verificar SHA-256 y firma GPG antes de instalar",
            "Uso recomendado": "Gestión seria, pruebas en equipo personal"
          },
          {
            "Tipo": "Extensión de navegador",
            "Dónde conseguirla": "Chrome Web Store / Firefox Add-ons",
            "Atención clave": "Número de descargas, ratings, editor verificado",
            "Uso recomendado": "Interacciones Web3 frecuentes pero con montos limitados"
          },
          {
            "Tipo": "Móvil (app store)",
            "Dónde conseguirla": "App Store / Google Play del país correcto",
            "Atención clave": "Verificar que sea la app original, revisar desarrollador",
            "Uso recomendado": "Uso diario, límites y alertas activadas"
          },
          {
            "Tipo": "Hardware wallet",
            "Dónde conseguirla": "Solo sitio oficial (fabricante)",
            "Atención clave": "Caja intacta, resellado sospechoso; generar semilla tú",
            "Uso recomendado": "Patrimonio a largo plazo (cold storage)"
          },
          {
            "Tipo": "Web wallet",
            "Dónde conseguirla": "Sitio oficial (evitar buscadores)",
            "Atención clave": "No ingresar seed; evitar uso prolongado",
            "Uso recomendado": "Casos puntuales, nunca como custodia principal"
          }
        ]
      }
    },

    {
      heading: "Buenas prácticas al instalar",
      list: [
        "Descarga, verifica integridad y **solo entonces** instala.",
        "Prefiere firma GPG cuando el proyecto la ofrece.",
        "En hardware wallets: compra en sitio oficial, verifica sellado, genera tú la seed.",
        "No instales extensiones clonadas con pocos ratings o nombres sospechosamente similares."
      ]
    },

    {
      heading: "Frase mnemotécnica (seed phrase)",
      text: [
        "Tras crear la billetera, interactuarás con la **frase mnemotécnica (seed)**, no con la clave privada directa.",
        "Estándares como **BIP-39** definen listas de 2048 palabras por idioma. Lo más común y suficiente son 12 o 24 palabras.",
        "Protege la generación: sin cámaras, gente alrededor ni grabaciones. Si será una cold wallet, considera hacerlo **offline**.",
        "Aunque las billeteras reputadas generan semilla con entropía adecuada, mantén el escepticismo y los hábitos de verificación."
      ],
      list: [
        "Nunca compartas tu seed ni la ingreses en sitios o DApps (solo en tu billetera).",
        "No la guardes en fotos ni en nubes; prefiere físico bien resguardado.",
        "Considera passphrase adicional si dominas su gestión (BIP-39)."
      ],
      subsections: [
        {
          heading: "Referencia de listas BIP-39",
          text: [
            "Las listas oficiales de palabras están disponibles en varios idiomas. Úsalas para entender el protocolo, no para memorizar combinaciones."
          ],
          code: "https://github.com/bitcoin/bips/blob/master/bip-0039/bip-0039-wordlists.md"
        }
      ]
    },

    {
      heading: "Checklist rápida",
      list: [
        "**¿Validaste que la URL es oficial (varias fuentes)?**",
        "**¿Comprobaste SHA-256 y, si existe, la firma GPG?**",
        "**¿Instalaste desde la tienda oficial del navegador o del país correcto?**",
        "**¿La extensión tiene millones de descargas y editor verificado?**",
        "**¿Compraste hardware wallet solo al fabricante?**",
        "**¿Generaste tú la seed phrase y la resguardaste correctamente?**"
      ]
    }
  ]
};
